import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useApp } from "@/context/AppContext";
import { Reveal } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";
import api from "@/lib/api";

export default function Home() {
  const { storefront } = useApp();
  const navigate = useNavigate();
  const [sections, setSections] = useState([]);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get(`/storefront/${storefront}/bootstrap`).then((r) => setSections(r.data.homepage || [])).catch(() => {});
    api.get(`/storefront/${storefront}/products`, { params: { featured: true } }).then((r) => setFeatured(r.data)).catch(() => {});
  }, [storefront]);

  return (
    <div>
      {sections.map((s, i) => {
        if (s.type === "hero") return <Hero key={i} s={s} navigate={navigate} />;
        if (s.type === "two_panel") return <TwoPanel key={i} s={s} navigate={navigate} />;
        if (s.type === "featured_collection") return <Featured key={i} s={s} navigate={navigate} />;
        if (s.type === "product_showcase") return <Showcase key={i} s={s} products={featured} />;
        if (s.type === "editorial") return <Editorial key={i} s={s} navigate={navigate} />;
        return null;
      })}
    </div>
  );
}

function Hero({ s, navigate }) {
  return (
    <section className="relative h-[90vh] min-h-[560px] img-zoom">
      <img src={s.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-black/20" />
      <div className="relative h-full flex flex-col items-center justify-center text-center text-white px-6">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}>
          <div className="eyebrow mb-6" style={{ color: "rgba(255,255,255,0.85)" }}>{s.eyebrow}</div>
          <h1 className="font-serif leading-[1.05] mb-6" style={{ fontSize: "clamp(2.4rem, 6vw, 4.4rem)" }}>{s.headline}</h1>
          <p className="max-w-xl mx-auto text-base md:text-lg font-light mb-10" style={{ color: "rgba(255,255,255,0.9)" }}>{s.text}</p>
          <button data-testid="hero-cta" onClick={() => navigate(s.cta_href)} className="btn-ghost">{s.cta_label}</button>
        </motion.div>
      </div>
    </section>
  );
}

function TwoPanel({ s, navigate }) {
  return (
    <section className="grid md:grid-cols-2">
      {(s.panels || []).map((p, i) => (
        <Reveal key={i} delay={i * 120} className="relative h-[70vh] min-h-[460px] img-zoom cursor-pointer" as="div">
          <button onClick={() => navigate(p.cta_href)} className="block w-full h-full">
            <img src={p.image} alt={p.title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/20 flex flex-col items-center justify-center text-white">
              <h2 className="font-serif text-4xl md:text-5xl tracking-wide mb-5">{p.title}</h2>
              <span className="eyebrow border-b border-white/70 pb-1">{p.cta_label}</span>
            </div>
          </button>
        </Reveal>
      ))}
    </section>
  );
}

function Featured({ s, navigate }) {
  return (
    <section className="relative h-[85vh] min-h-[520px] img-zoom">
      <img src={s.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-black/30" />
      <Reveal className="relative h-full flex flex-col items-center justify-center text-center text-white px-6">
        <div className="eyebrow mb-5" style={{ color: "rgba(255,255,255,0.85)" }}>{s.eyebrow}</div>
        <h2 className="font-serif text-4xl md:text-6xl mb-5">{s.headline}</h2>
        <p className="max-w-lg text-base font-light mb-9" style={{ color: "rgba(255,255,255,0.9)" }}>{s.text}</p>
        <button data-testid="featured-cta" onClick={() => navigate(s.cta_href)} className="btn-ghost">{s.cta_label}</button>
      </Reveal>
    </section>
  );
}

function Showcase({ s, products }) {
  if (!products.length) return null;
  return (
    <section className="max-w-[1500px] mx-auto px-6 py-24">
      <Reveal className="text-center mb-14">
        <div className="eyebrow mb-3" style={{ color: "var(--ev-muted)" }}>Maison Selection</div>
        <h2 className="font-serif text-3xl md:text-5xl">{s.headline}</h2>
      </Reveal>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
        {products.slice(0, 4).map((p, i) => <Reveal key={p.id} delay={i * 80}><ProductCard product={p} /></Reveal>)}
      </div>
    </section>
  );
}

function Editorial({ s, navigate }) {
  return (
    <section className="grid md:grid-cols-2 items-stretch" style={{ background: "var(--ev-surface)" }}>
      <Reveal className="img-zoom h-[60vh] md:h-auto">
        <img src={s.image} alt="" className="w-full h-full object-cover" />
      </Reveal>
      <Reveal className="flex flex-col justify-center px-8 md:px-20 py-20" delay={120}>
        <div className="eyebrow mb-5" style={{ color: "var(--ev-muted)" }}>{s.eyebrow}</div>
        <h2 className="font-serif text-3xl md:text-5xl leading-tight mb-6">{s.headline}</h2>
        <p className="text-base leading-relaxed mb-9 max-w-md" style={{ color: "var(--ev-muted)" }}>{s.text}</p>
        <button data-testid="editorial-cta" onClick={() => navigate(s.cta_href)} className="btn-luxe self-start">{s.cta_label}</button>
      </Reveal>
    </section>
  );
}
